import type { PlotRequest } from "@tento-data";
import { PlotCollection } from "@tento-data";

import type { CanvasRenderTarget } from "../canvas/canvas-types";
import type { CanvasSeriesRenderer } from "./renderer";
import { effectiveYExtent, toCanvasX, toCanvasY } from "./scale-utils";

export type LineRendererStyle = Readonly<{
  strokeStyle: string;
  lineWidth: number;
}>;

/** Canvas line renderer for scalar plot series. */
export class LineRenderer implements CanvasSeriesRenderer {
  constructor(private readonly style: LineRendererStyle = {
    strokeStyle: "#2563eb",
    lineWidth: 2,
  }) {}

  supports(entry: PlotCollection.AnyEntry): boolean {
    return PlotCollection.isScalarEntry(entry);
  }

  render(
    target: CanvasRenderTarget,
    request: PlotRequest,
    entry: PlotCollection.AnyEntry,
  ): void {
    if (!PlotCollection.isScalarEntry(entry)) return;

    const points = entry.series.range(
      request.paddedRange.start,
      request.paddedRange.end,
    ).points();
    const extent = effectiveYExtent(entry, request, request.paddedRange);

    if (points.length === 0 || extent === null || extent === undefined) return;

    target.ctx.strokeStyle = this.style.strokeStyle;
    target.ctx.lineWidth = this.style.lineWidth;
    target.ctx.beginPath();

    for (let i = 0; i < points.length; i++) {
      const [x, y] = points[i];
      const canvasX = toCanvasX(x, request.viewRange, target.size.width);
      const canvasY = toCanvasY(y, extent, target.size.height);

      if (i === 0) {
        target.ctx.moveTo(canvasX, canvasY);
      } else {
        target.ctx.lineTo(canvasX, canvasY);
      }
    }

    target.ctx.stroke();
  }
}
